import * as React from 'react';
import { Button, Icon, Tooltip } from 'kui';
import './_Snackbar.scss';
import FocusLock from 'react-focus-lock';
import { getParentsClasses } from '../../../helpers/getParentsClasses';
import { ISnackbarProps } from './types';
import { CLASS_SNACKBAR } from './constants';
import { returnFocus } from '../../../helpers/returnFocus';
import ReactHtmlParser from 'react-html-parser';

export const Snackbar = ({
    buttons,
    icon,
    isNoTrim,
    text,
    timer,
    title,
    onTimer
}: ISnackbarProps) => {
    const className = CLASS_SNACKBAR;
    const classButton = className + '__button';
    const classTitle = className + '__title';
    const classContent = className + '__content';
    const classTooltip = className + '__tooltip';

    const timerRef = React.useRef(null);
    const titleRef = React.useRef(null);
    const textRef = React.useRef(null);

    let isProgressive = buttons && buttons.length && !!buttons.find(button => button.isTimer || button.progress);

    // FocusLock нужен, если 2 кнопки, т.е. требуется действие пользователя
    const [isFocusLockDisabled, setFocusLockDisabled] = React.useState(!buttons || buttons.length < 2);
    const [timerHook, setTimerHook] = React.useState(timer && timer * 10);
    const [titleTooltip, setTitleTooltip] = React.useState(null);
    const [textTooltip, setTextTooltip] = React.useState(null);
    let [isHovered, setHovered] = React.useState(null);
    const [s] = React.useState<any>({});
    s.isHovered = isHovered;

    function onBlur (event: React.FocusEvent) {
        if (timerRef.current) return;

        const targetClasses = getParentsClasses(
            event.relatedTarget as HTMLElement,
            ['snackbar']
        );
        if (targetClasses.includes('kui-snackbar')) return;
        setFocusLockDisabled(true); // если кликнули вне снекбара, отключаем FocusLock
    }

    function onKeyDown (event: React.KeyboardEvent) {
        if (event.key === 'Tab') { // если нажали Tab, то не ловим onBlur
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                timerRef.current = null;
            }, 200);
        }
    }

    React.useEffect(() => {
        if (isNoTrim) return;

        const titleEl = titleRef.current as HTMLElement;
        if (titleEl && titleEl.offsetHeight < titleEl.scrollHeight) {
            setTitleTooltip(title);
        }

        const textEl = textRef.current as HTMLElement;
        if (textEl && textEl.offsetHeight < textEl.scrollHeight) {
            setTextTooltip(text);
        }
    }, []);

    const timeout = React.useRef(null);
    React.useEffect(() => {
        let unmounted = false;
        if (timerHook === null) return;
        timeout.current = setInterval(() => {
            if (unmounted) return;
            if (timerHook < 1) {
                if (onTimer) onTimer();
                clearInterval(timeout.current);
            } else if (!s.isHovered) {
                setTimerHook(timerHook => timerHook - 1);
            }
        }, 100);

        return () => {
            unmounted = true;
            clearInterval(timeout.current);
        }
    }, [timerHook]);

    let buttonsGroup = [],
        buttonsGroupDiv = null;

    if (buttons) {
        buttonsGroup = buttons.map((item, index) => {
            const {
                isPrimary,
                isTimer,
                progress,
                text,
                ...attributes
            } = item;

            return (
                <Button
                    className={`
                        ${classButton}
                        ${isPrimary ? classButton + '--primary' : ''}
                        ${isTimer ? classButton + '--timer' : ''}
                    `}
                    color={'white'}
                    key={index}
                    variant={'primary'}
                    progress={progress ? progress : isTimer && timer ? 100 - timerHook / timer * 10 : null}
                    {...attributes}
                >
                    {text}
                </Button>
            );
        });
        buttonsGroupDiv = (
            <div className={className + '__buttons'}>
                {buttonsGroup}
            </div>
        );
    }

    const iconEl = icon && <Icon xlink={icon} size={24} className={className + '__icon'} />;

    return (
        <FocusLock
            disabled={isFocusLockDisabled}
            returnFocus={returnFocus}
        >
            <div
                className={`
                        ${className}
                        ${!title ? className + '--notitle' : ''}
                        ${!isNoTrim ? className + '--trim' : ''}
                        ${!isProgressive && !isHovered && timerHook !== null && timerHook < 3 ? className+ '--fadeout' : ''}
                        ${(isProgressive || !isHovered) && timerHook !== null && timerHook <= 0 ? className + '--hidden' : ''}
                    `}
                tabIndex={0}
                aria-live={timer === timerHook ? 'assertive' : 'off'} // при смене таймера скрин ридеру нужен только таймер
                role={'alert'}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
            >
                <div className={className + '__body'}>
                    {title &&
                        <Tooltip value={titleTooltip} direction={'right'} isNoEvents className={classTooltip}>
                            <div className={classTitle} ref={titleRef}>
                                {iconEl}
                                <div className={classTitle + '-text'}>
                                    {ReactHtmlParser(title)}
                                </div>
                            </div>
                        </Tooltip>
                    }
                    {text &&
                        <Tooltip value={textTooltip} direction={'right'} isNoEvents className={classTooltip}>
                            <div className={classContent} ref={textRef}>
                                {!title && iconEl}
                                <div className={classContent + '-text'}>
                                    {ReactHtmlParser(text)}
                                </div>
                            </div>
                        </Tooltip>
                    }
                </div>
                {buttonsGroupDiv}
            </div>
        </FocusLock>
    );
};
